import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemText, TextField, Button, Card, CardContent, CardMedia, Input, ListItemIcon, FormControl, Select, MenuItem, InputAdornment, IconButton, Tooltip } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import { styled } from '@mui/material/styles';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationSelector from '../components/LocationSelector';
import serviceBazarNp from '../i18n/np/serviceBazar';
import serviceBazarPk from '../i18n/pk/serviceBazar';
import { 
  Search,
  Upload,
  LocationOn,
  Close,
  Engineering,
  Plumbing,
  Build,
  Construction,
  Brush,
  Grid4x4,
  LocalCarWash,
  Carpenter,
  DirectionsCar,
  AcUnit,
  WaterDrop,
  Videocam,
  Roofing,
  AgricultureRounded,
  Store,
  Handyman
} from '@mui/icons-material';

// Add keyframes for cursor blinking
const keyframes = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

// Inject the keyframes into the document
const injectGlobalStyles = () => {
  const styleEl = document.createElement('style');
  styleEl.innerHTML = keyframes;
  document.head.appendChild(styleEl);
  return () => document.head.removeChild(styleEl);
};

// Styled components
const Sidebar = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  maxWidth: '260px',
  width: '100%',
}));

const CategoryList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const StoreList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
  color: 'white',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const MainContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  height: 'calc(100vh - 180px)',
  overflowY: 'auto',
}));

const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 8px',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 'bold',
}));

const SearchBarHeader = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  overflow: 'hidden',
  zIndex: 1,
}));

const VideoBackground = styled('video')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: -1,
});

const SearchOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(45deg, rgba(94, 53, 177, 0.82) 30%, rgba(30, 136, 229, 0.72) 90%)',
  zIndex: -1,
}));

// Default logo component for service providers
const DefaultServiceLogo = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  backgroundColor: '#fff3e0',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #ffcc02',
}));

// Service categories with icons
const categories = [
  { name: 'ইলেকট্রিশিয়ান', englishName: 'Electrician', icon: <Engineering /> },
  { name: 'প্লাম্বার', englishName: 'Plumber', icon: <Plumbing /> },
  { name: 'কাঠ মিস্ত্রি', englishName: 'Carpenter', icon: <Carpenter /> },
  { name: 'মিস্ত্রি', englishName: 'Mason/Construction Worker', icon: <Construction /> },
  { name: 'রঙ মিস্ত্রি', englishName: 'Painter', icon: <Brush /> },
  { name: 'টাইলস মিস্ত্রি', englishName: 'Tiler', icon: <Grid4x4 /> },
  { name: 'ওয়েল্ডার', englishName: 'Welder', icon: <Handyman /> },
  { name: 'ফার্নিচার মিস্ত্রি', englishName: 'Furniture Maker', icon: <Build /> },
  { name: 'গাড়ি মেকানিক', englishName: 'Car Mechanic', icon: <DirectionsCar /> },
  { name: 'এসি টেকনিশিয়ান', englishName: 'AC Technician', icon: <AcUnit /> },
  { name: 'পানির পাম্প মেকানিক', englishName: 'Water Pump Mechanic', icon: <WaterDrop /> },
  { name: 'সিসিটিভি টেকনিশিয়ান', englishName: 'CCTV Technician', icon: <Videocam /> },
  { name: 'ছাদ ঠিককারী', englishName: 'Roofer', icon: <Roofing /> },
  { name: 'কৃষি সহায়তাকারি', englishName: 'Farmer Help', icon: <AgricultureRounded /> },
];

// Mock service providers
const serviceProviders = [
  {
    name: 'Expert Solutions',
    logo: '/images/logos/expert.png',
    description: 'পেশাদার ও দক্ষ কারিগর সেবা'
  },
  {
    name: 'Quick Fix Services',
    logo: '/images/logos/quickfix.png',
    description: 'দ্রুত ও নির্ভরযোগ্য সমাধান'
  },
  {
    name: 'Professional Repairs',
    logo: '/images/logos/professional.png',
    description: 'উন্নত প্রযুক্তি ও অভিজ্ঞতা'
  },
  {
    name: 'Home Care Services',
    logo: '/images/logos/homecare.png',
    description: 'বাড়িতে বসে সব ধরনের সেবা'
  },
  {
    name: 'City Services Ltd',
    logo: '/images/logos/cityservices.png',
    description: 'শহরব্যাপী সেবা নেটওয়ার্ক'
  },
  {
    name: 'Reliable Solutions',
    logo: '/images/logos/reliable.png',
    description: 'বিশ্বস্ত ও মানসম্পন্ন কাজ'
  },
  {
    name: 'Fast & Fix',
    logo: '/images/logos/fastfix.png',
    description: 'জরুরি সেবা ও দ্রুত সমাধান'
  },
  {
    name: 'Pro Service Hub',
    logo: '/images/logos/prohub.png',
    description: 'সব ধরনের পেশাদার সেবা'
  },
];

// Mock services data
const services = [
  {
    id: 1,
    name: 'ইলেকট্রিক সার্ভিস',
    englishName: 'Electric Service',
    category: 'ইলেকট্রিশিয়ান',
    provider: 'Expert Solutions',
    price: '৳৫০০/ঘন্টা',
    image: '/images/electrician.jpeg',
    description: 'বাড়ি ও অফিসের সকল ধরনের ইলেকট্রিক সমস্যা সমাধান',
    experience: '৫ বছর'
  },
  {
    id: 2,
    name: 'প্লাম্বিং সার্ভিস',
    englishName: 'Plumbing Service',
    category: 'প্লাম্বার',
    provider: 'Quick Fix Services',
    price: '৳৪০০/ঘন্টা',
    image: '/images/plumbing.jpg',
    description: 'পানি লাইন, ড্রেন এবং সেনিটারি সমস্যার সমাধান',
    experience: '৭ বছর'
  },
  // Add more mock services as needed
];

// All 64 districts of Bangladesh with both Bangla and English names
const districts = [
  { bn: 'সকল জেলা', en: 'All Districts' },
  { bn: 'ঢাকা', en: 'Dhaka' },
  { bn: 'ফরিদপুর', en: 'Faridpur' },
  { bn: 'গাজীপুর', en: 'Gazipur' },
  { bn: 'গোপালগঞ্জ', en: 'Gopalganj' },
  { bn: 'জামালপুর', en: 'Jamalpur' },
  { bn: 'কিশোরগঞ্জ', en: 'Kishoreganj' },
  { bn: 'মাদারীপুর', en: 'Madaripur' },
  { bn: 'মানিকগঞ্জ', en: 'Manikganj' },
  { bn: 'মুন্সিগঞ্জ', en: 'Munshiganj' },
  { bn: 'ময়মনসিংহ', en: 'Mymensingh' },
  { bn: 'নারায়ণগঞ্জ', en: 'Narayanganj' },
  { bn: 'নরসিংদী', en: 'Narsingdi' },
  { bn: 'নেত্রকোনা', en: 'Netrokona' },
  { bn: 'রাজবাড়ী', en: 'Rajbari' },
  { bn: 'শরীয়তপুর', en: 'Shariatpur' },
  { bn: 'শেরপুর', en: 'Sherpur' },
  { bn: 'টাঙ্গাইল', en: 'Tangail' },
  { bn: 'বগুড়া', en: 'Bogura' },
  { bn: 'জয়পুরহাট', en: 'Joypurhat' },
  { bn: 'নওগাঁ', en: 'Naogaon' },
  { bn: 'নাটোর', en: 'Natore' },
  { bn: 'চাঁপাইনবাবগঞ্জ', en: 'Chapainawabganj' },
  { bn: 'পাবনা', en: 'Pabna' },
  { bn: 'রাজশাহী', en: 'Rajshahi' },
  { bn: 'সিরাজগঞ্জ', en: 'Sirajganj' },
  { bn: 'দিনাজপুর', en: 'Dinajpur' },
  { bn: 'গাইবান্ধা', en: 'Gaibandha' },
  { bn: 'কুড়িগ্রাম', en: 'Kurigram' },
  { bn: 'লালমনিরহাট', en: 'Lalmonirhat' },
  { bn: 'নীলফামারী', en: 'Nilphamari' },
  { bn: 'পঞ্চগড়', en: 'Panchagarh' },
  { bn: 'রংপুর', en: 'Rangpur' },
  { bn: 'ঠাকুরগাঁও', en: 'Thakurgaon' },
  { bn: 'বরগুনা', en: 'Barguna' },
  { bn: 'বরিশাল', en: 'Barishal' },
  { bn: 'ভোলা', en: 'Bhola' },
  { bn: 'ঝালকাঠি', en: 'Jhalokati' },
  { bn: 'পটুয়াখালী', en: 'Patuakhali' },
  { bn: 'পিরোজপুর', en: 'Pirojpur' },
  { bn: 'বান্দরবান', en: 'Bandarban' },
  { bn: 'ব্রাহ্মণবাড়িয়া', en: 'Brahmanbaria' },
  { bn: 'চাঁদপুর', en: 'Chandpur' },
  { bn: 'চট্টগ্রাম', en: 'Chattogram' },
  { bn: 'কুমিল্লা', en: 'Cumilla' },
  { bn: 'কক্সবাজার', en: "Cox's Bazar" },
  { bn: 'ফেনী', en: 'Feni' },
  { bn: 'খাগড়াছড়ি', en: 'Khagrachhari' },
  { bn: 'লক্ষ্মীপুর', en: 'Lakshmipur' },
  { bn: 'নোয়াখালী', en: 'Noakhali' },
  { bn: 'রাঙ্গামাটি', en: 'Rangamati' },
  { bn: 'হবিগঞ্জ', en: 'Habiganj' },
  { bn: 'মৌলভীবাজার', en: 'Moulvibazar' },
  { bn: 'সুনামগঞ্জ', en: 'Sunamganj' },
  { bn: 'সিলেট', en: 'Sylhet' },
  { bn: 'বাগেরহাট', en: 'Bagerhat' },
  { bn: 'চুয়াডাঙ্গা', en: 'Chuadanga' },
  { bn: 'যশোর', en: 'Jashore' },
  { bn: 'ঝিনাইদহ', en: 'Jhenaidah' },
  { bn: 'খুলনা', en: 'Khulna' },
  { bn: 'কুষ্টিয়া', en: 'Kushtia' },
  { bn: 'মাগুরা', en: 'Magura' },
  { bn: 'মেহেরপুর', en: 'Meherpur' },
  { bn: 'নড়াইল', en: 'Narail' },
  { bn: 'সাতক্ষীরা', en: 'Satkhira' }
];

// Mock upazillas data with district associations
const upazillasByDistrict = {
  'ঢাকা': [
    { bn: 'সকল উপজেলা', en: 'All Upazilas' },
    { bn: 'ধানমন্ডি', en: 'Dhanmondi' },
    { bn: 'মিরপুর', en: 'Mirpur' },
    { bn: 'মোহাম্মদপুর', en: 'Mohammadpur' },
    { bn: 'গুলশান', en: 'Gulshan' },
    { bn: 'বনানী', en: 'Banani' },
    { bn: 'উত্তরা', en: 'Uttara' },
    { bn: 'রমনা', en: 'Ramna' },
    { bn: 'মতিঝিল', en: 'Motijheel' },
    { bn: 'খিলগাঁও', en: 'Khilgaon' },
    { bn: 'সাভার', en: 'Savar' },
    { bn: 'কেরাণীগঞ্জ', en: 'Keraniganj' },
    { bn: 'ধামরাই', en: 'Dhamrai' },
    { bn: 'দোহার', en: 'Dohar' },
    { bn: 'নবাবগঞ্জ', en: 'Nawabganj' }
  ],
  'চট্টগ্রাম': [
    { bn: 'সকল উপজেলা', en: 'All Upazilas' },
    { bn: 'আগ্রাবাদ', en: 'Agrabad' },
    { bn: 'চান্দগাঁও', en: 'Chandgaon' },
    { bn: 'হালিশহর', en: 'Halishahar' },
    { bn: 'পতেঙ্গা', en: 'Patenga' },
    { bn: 'বায়েজিদ', en: 'Bayejid' },
    { bn: 'সীতাকুন্ড', en: 'Sitakunda' },
    { bn: 'মিরসরাই', en: 'Mirsharai' },
    { bn: 'পটিয়া', en: 'Patiya' },
    { bn: 'সন্দ্বীপ', en: 'Sandwip' },
    { bn: 'আনোয়ারা', en: 'Anwara' },
    { bn: 'চন্দনাইশ', en: 'Chandanaish' },
    { bn: 'সাতকানিয়া', en: 'Satkania' },
    { bn: 'লোহাগাড়া', en: 'Lohagara' },
    { bn: 'বাঁশখালী', en: 'Banshkhali' },
    { bn: 'বোয়ালখালী', en: 'Boalkhali' }
  ],
};

// Add a default set of upazillas for districts we haven't fully mapped
const defaultUpazillas = [
  { bn: 'সকল উপজেলা', en: 'All Upazilas' },
  { bn: 'সদর উপজেলা', en: 'Sadar Upazila' },
  { bn: 'উপশহর', en: 'Upashahar' },
  { bn: 'পৌরসভা', en: 'Pourashava' }
];

// Default upazillas for "All Districts" option
const allUpazillas = [
  { bn: 'সকল উপজেলা', en: 'All Upazilas' },
  { bn: 'ধানমন্ডি', en: 'Dhanmondi' },
  { bn: 'মিরপুর', en: 'Mirpur' },
  { bn: 'গুলশান', en: 'Gulshan' },
  { bn: 'উত্তরা', en: 'Uttara' },
  { bn: 'মোহাম্মদপুর', en: 'Mohammadpur' },
  { bn: 'বনানী', en: 'Banani' },
  { bn: 'সিলেট সদর', en: 'Sylhet Sadar' },
  { bn: 'চট্টগ্রাম সদর', en: 'Chattogram Sadar' },
  { bn: 'খুলনা সদর', en: 'Khulna Sadar' },
  { bn: 'রাজশাহী সদর', en: 'Rajshahi Sadar' }
];

// List of bazars in Bangla
const bazars = [
  { bn: 'সকল বাজার', en: 'All Bazars' },
  { bn: 'চাচকৈড় বাজার', en: 'Chachkoir Bazar' },
  { bn: 'খুবজিপুর বাজার', en: 'Khubipur Bazar' },
  { bn: 'নাজিরপুর বাজার', en: 'Nazirpur Bazar' },
  { bn: 'বিলদোহর বাজার', en: 'Bildohar Bazar' },
  { bn: 'চাটখিল বাজার', en: 'Chatkhil Bazar' },
  { bn: 'সোনাইমুড়ি বাজার', en: 'Sonaimuri Bazar' },
  { bn: 'কেন্দ্রীয় বাজার', en: 'Central Bazar' },
  { bn: 'নিউ মার্কেট', en: 'New Market' },
  { bn: 'গুলিস্তান বাজার', en: 'Gulistan Bazar' },
  { bn: 'মৌচাক বাজার', en: 'Mouchak Bazar' },
  { bn: 'রকেট বাজার', en: 'Rocket Bazar' },
  { bn: 'কাওরান বাজার', en: 'Kawran Bazar' },
  { bn: 'ফার্মগেট বাজার', en: 'Farmgate Bazar' },
  { bn: 'মিরপুর বাজার', en: 'Mirpur Bazar' },
  { bn: 'উত্তরা বাজার', en: 'Uttara Bazar' },
  { bn: 'ধানমন্ডি বাজার', en: 'Dhanmondi Bazar' },
  { bn: 'মোহাম্মদপুর বাজার', en: 'Mohammadpur Bazar' },
  { bn: 'বনানী বাজার', en: 'Banani Bazar' },
  { bn: 'গুলশান বাজার', en: 'Gulshan Bazar' }
];

const ServiceBazar = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUpazilla, setSelectedUpazilla] = useState('সকল উপজেলা');
  const [selectedDistrict, setSelectedDistrict] = useState('সকল জেলা');
  const [selectedBazar, setSelectedBazar] = useState('সকল বাজার');
  const [districtSearchQuery, setDistrictSearchQuery] = useState('');
  const [upazillaSearchQuery, setUpazillaSearchQuery] = useState('');
  const [bazarSearchQuery, setBazarSearchQuery] = useState('');
  const [availableUpazillas, setAvailableUpazillas] = useState(allUpazillas);
  const [typingText, setTypingText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);

  const lang = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
  const t = lang === 'np' ? serviceBazarNp : lang === 'pk' ? serviceBazarPk : null;
  const searchPlaceholders = t ? [t.search.placeholder, t.search.placeholder, t.search.placeholder] : [
    'সার্ভিসের নাম লিখুন...',
    'সার্ভিস প্রদানকারির নাম লিখুন...',
    'সার্ভিসের ধরন লিখুন...',
  ];

  // Inject keyframes for the blinking cursor animation
  useEffect(() => {
    const cleanup = injectGlobalStyles();
    return cleanup;
  }, []);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const handleProviderClick = (provider) => {
    setSelectedProvider(provider.name);
  };

  const handleDistrictChange = (event) => {
    const district = event.target.value;
    setSelectedDistrict(district);
    
    // Reset upazilla when district changes
    setSelectedUpazilla('সকল উপজেলা');
    setUpazillaSearchQuery('');
    
    // Update available upazillas based on selected district
    if (district === 'সকল জেলা') {
      setAvailableUpazillas(allUpazillas);
    } else {
      // Look up the district's upazillas, or use defaults if not specifically mapped
      const districtUpazillas = upazillasByDistrict[district] || defaultUpazillas;
      setAvailableUpazillas(districtUpazillas);
    }
  };

  const handleUpazillaChange = (event) => {
    setSelectedUpazilla(event.target.value);
  };

  const handleBazarChange = (event) => {
    setSelectedBazar(event.target.value);
  };

  // Filter districts based on search query
  const filteredDistricts = districts.filter(
    district => district.bn.toLowerCase().includes(districtSearchQuery.toLowerCase())
  );

  // Filter upazillas based on search query
  const filteredUpazillas = availableUpazillas.filter(
    upazilla => upazilla.bn.toLowerCase().includes(upazillaSearchQuery.toLowerCase())
  );

  // Filter bazars based on search query
  const filteredBazars = bazars.filter(
    bazar => bazar.bn.toLowerCase().includes(bazarSearchQuery.toLowerCase())
  );

  // Custom dropdown render props
  const districtMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      },
    },
  };

  const upazillaMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      },
    },
  };

  const filteredServices = services.filter((service) => {
    const matchesCategory = !selectedCategory || service.category === selectedCategory;
    const matchesProvider = !selectedProvider || service.provider === selectedProvider;
    const matchesSearch = !searchQuery || 
      service.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesProvider && matchesSearch;
  });

  return (
    <>
      <Header />
      <Box className="service-bazar-container" sx={{ flexGrow: 1, p: 2 }}>
        {/* Search Header Bar */}
        <SearchBarHeader>
          <VideoBackground autoPlay muted loop>
            <source src="/videos/service.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </VideoBackground>
          <SearchOverlay />          {/* Location Selector */}
          <LocationSelector 
            variant="search-bar"
            onLocationChange={(location) => {
              // Optional: Handle location change if needed
              console.log('Location changed:', location);
            }}
          />

          {/* Search Field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="সার্ভিস খুঁজুন..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              mx: 2, 
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              },
              zIndex: 2,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />
        </SearchBarHeader>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Sidebar */}
          <Box sx={{ 
            width: { xs: '100%', md: '260px' }, 
            minWidth: { md: '260px' },
            pr: { md: 2 }, 
            mb: { xs: 2, md: 0 } 
          }}>
            <Sidebar elevation={3}>
              <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader>
                  <Build sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    {t ? t.sidebar.categoriesTitle : 'সার্ভিসের ধরন'}
                  </Typography>
                </SectionHeader>
                <CategoryList sx={{ pl: 1, pr: 1 }}>
                  {categories.map((category) => (
                    <ListItem
                      button
                      key={category.name}
                      selected={selectedCategory === category.name}
                      onClick={() => handleCategoryClick(category.name)}
                    >
                      <ListItemIcon>
                        {category.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={category.name} 
                        secondary={category.englishName}
                        primaryTypographyProps={{ style: { fontWeight: selectedCategory === category.name ? 'bold' : 'normal' } }}
                      />
                    </ListItem>
                  ))}
                </CategoryList>

                <SectionHeader sx={{ mt: 2 }}>
                  <Store sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    {t ? t.sidebar.providersTitle : 'সার্ভিস প্রদানকারি'}
                  </Typography>
                </SectionHeader>
                <StoreList sx={{ pl: 1, pr: 1 }}>
                  {serviceProviders.map((provider) => (
                    <ListItem
                      button
                      key={provider.name}
                      selected={selectedProvider === provider.name}
                      onClick={() => handleProviderClick(provider)}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Box
                            component="img"
                            src={provider.logo}
                            alt={provider.name}
                            sx={{
                              width: 20,
                              height: 20,
                              objectFit: 'contain',
                              borderRadius: 1,
                              display: 'block'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                            onLoad={(e) => {
                              e.target.style.display = 'block';
                              e.target.nextSibling.style.display = 'none';
                            }}
                          />
                          <DefaultServiceLogo sx={{ display: 'none' }}>
                            <Build sx={{ fontSize: 12, color: '#f57c00' }} />
                          </DefaultServiceLogo>
                        </Box>
                      </ListItemIcon>
                      <ListItemText 
                        primary={provider.name}
                        secondary={provider.description}
                        primaryTypographyProps={{ 
                          fontSize: '0.875rem',
                          fontWeight: selectedProvider === provider.name ? 'bold' : 'normal' 
                        }}
                        secondaryTypographyProps={{ 
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                </StoreList>
              </Box>
            </Sidebar>
          </Box>

          {/* Main Content */}
          <Box sx={{ 
            width: { xs: '100%', md: 'calc(100% - 260px)' },
            flexGrow: 1 
          }}>
            <MainContent>
              {/* Service Grid */}
              {filteredServices.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)', // Mobile portrait: 1 card per row
                      sm: 'repeat(2, 1fr)', // Mobile horizontal: 2 cards per row
                      md: 'repeat(5, 1fr)', // Laptop: 6 cards per row
                      xl: 'repeat(6, 1fr)', // Large screens: 6 cards per row
                      '@media (min-width: 1980px)': {
                        gridTemplateColumns: 'repeat(9, 1fr)' // Extra large screens: 9 cards per row
                      }
                    },
                    width: '100%',
                  }}
                >
                  {filteredServices.map((service) => (
                    <Box key={service.id} sx={{ display: 'flex', width: '100%' }}>
                      <ServiceCard sx={{ width: '100%' }}>
                        <Box sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="160"
                            image={service.image}
                            alt={service.name}
                            sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5', pt: 1 }}
                          />
                          <StyledBadge>{service.category}</StyledBadge>
                        </Box>
                        <CardContent sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          pb: '16px !important' // Override default padding bottom
                        }}>
                          <Box>
                            <Typography gutterBottom variant="h6" component="div">
                              {service.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {service.englishName}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.primary">
                                <strong>সার্ভিস প্রদানকারী:</strong> {service.provider}
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                <strong>অভিজ্ঞতা:</strong> {service.experience}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{
                            mt: 'auto', // Push this box to the bottom
                            pt: 2 // Add some spacing from the content above
                          }}>
                            <Typography variant="h6" color="primary" sx={{ mb: 1, textAlign: 'center' }}>
                              {service.price}
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1
                            }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                fullWidth
                                sx={{ whiteSpace: 'nowrap', px: 1.5, py: 0.75, minWidth: 'auto', lineHeight: 1 }}
                              >
                                {t ? t.card.book : 'বুক করুন'}
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                fullWidth
                                sx={{ whiteSpace: 'nowrap', px: 1.5, py: 0.75, minWidth: 'auto', lineHeight: 1 }}
                              >
                                {t ? t.card.contact : 'যোগাযোগ করুন'}
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </ServiceCard>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                  <Build sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t ? t.empty.title : 'কোন সার্ভিস পাওয়া যায়নি'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t ? t.empty.subtitle : 'অনুগ্রহ করে আপনার ফিল্টার বা সার্চ পরিবর্তন করুন'}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 2 }}
                    onClick={() => {
                      setSelectedCategory('');
                      setSelectedProvider('');
                      setSearchQuery('');
                      setSelectedBazar('সকল বাজার');
                    }}
                  >
                    {t ? t.empty.clear : 'সব ফিল্টার মুছুন'}
                  </Button>
                </Paper>
              )}
            </MainContent>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: "50px" }}>
        <Footer />
      </Box>
    </>
  );
};

export default ServiceBazar;
